import React from "react";
import { Loader2, X } from "lucide-react";
 
import SearchableSelect from "../../../../components/SearchableSelect";
import type {
  CourtRetentionSettings,
  RetentionDraft,
} from "./RetentionSetting.types";
 
interface RetentionSettingsModalProps {
  setting: CourtRetentionSettings;
  draft: RetentionDraft;
  isSaving: boolean;
  onClose: () => void;
  onFieldChange: (
    id: string,
    field: keyof CourtRetentionSettings,
    value: RetentionDraft[keyof RetentionDraft],
  ) => void;
  onSave: () => void;
}
const hourOptions = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour).padStart(2, "0"),
  label: String(hour).padStart(2, "0"),
}));
 
const getHourLabel = (value: string) => {
  if (!value) return "";
 
  const hour = Number(value);
  if (Number.isNaN(hour)) return "";
 
  return String(hour).padStart(2, "0");
};
 
const retentionDayOptions = Array.from({ length: 31 }, (_, day) => ({
  value: String(day),
  label: `${day} Day${day === 1 ? "" : "s"}`,
}));
 
const minuteOptions = Array.from({ length: 60 }, (_, minute) => ({
  value: String(minute).padStart(2, "0"),
  label: String(minute).padStart(2, "0"),
}));
 
const secondOptions = Array.from({ length: 60 }, (_, second) => ({
  value: String(second).padStart(2, "0"),
  label: String(second).padStart(2, "0"),
}));
 
const getRetentionDayLabel = (value: string) => {
  if (!value) return "";
 
  const days = Number(value);
  if (Number.isNaN(days)) return "";
 
  return `${days} Day${days === 1 ? "" : "s"}`;
};
 
const getMinuteLabel = (value: string) => {
  if (!value) return "";
 
  const minute = Number(value);
  if (Number.isNaN(minute)) return "";
 
  return String(minute).padStart(2, "0");
};
 
const getSecondLabel = (value: string) => {
  if (!value) return "";
 
  const second = Number(value);
  if (Number.isNaN(second)) return "";
 
  return String(second).padStart(2, "0");
};
 
const getArchiveTimeParts = (value: string) => {
  const parts = value.split(":");
 
  return {
    hour: parts[0] || "",
    minute: parts[1] || "",
    second: parts[2] || "",
  };
};
 
const getArchiveTimeValue = (hour: string, minute: string, second: string) => {
  if (!hour && !minute && !second) return "";
 
  return `${hour || "00"}:${minute || "00"}:${second || "00"}`;
};
 
const RetentionSettingsModal: React.FC<RetentionSettingsModalProps> = ({
  setting,
  draft,
  isSaving,
  onClose,
  onFieldChange,
  onSave,
}) => {
  const retentionDays = String(draft.retentionDays ?? setting.retentionDays);
  const retentionTimeUnit = String(
    (draft.retentionTimeUnit ?? setting.retentionTimeUnit) || "",
  );
  const archiveTime = getArchiveTimeParts(retentionTimeUnit);
  const description = (draft.description ?? setting.description) || "";
  const isActive = (draft.isActive ?? setting.isActive) || false;
  const hasSavedSettings =
    setting.retentionDays !== 0 || Boolean(setting.description);
 
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#9CA3AF] flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">
            {hasSavedSettings
              ? "Edit Retention Setting"
              : "Add Retention Setting"}
          </h3>
          <button
            onClick={onClose}
            className="text-[#3e3e3e] w-8 h-8 p-1 rounded-full font-bold cursor-pointer border border-[#D0D0D0] hover:border-[#666666] hover:text-[#000000]"
          >
            <X size={20}/>
          </button>
        </div>
 
        <div className="p-6 space-y-6">
          <div>
            <label className="text-sm font-medium text-gray-600 mb-2 block">
              Court
            </label>
            <input
              type="text"
              value={setting.courtType}
              disabled
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-700"
            />
          </div>
 
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div>
              <label className="text-sm font-medium text-gray-600 mb-2 block">
                Retention Period (Days) *
              </label>
              <SearchableSelect
                placeholder="Select Days"
                searchPlaceholder="Search days..."
                options={retentionDayOptions}
                value={{
                  value: retentionDays,
                  label: getRetentionDayLabel(retentionDays),
                }}
                onChange={(option: any) =>
                  onFieldChange(
                    setting._id,
                    "retentionDays",
                    option?.value || "",
                  )
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 mb-2 block">
                Archive Hour *
              </label>
              <SearchableSelect
                placeholder="Select Hour"
                searchPlaceholder="Search hour..."
                options={hourOptions}
                value={{
                  value: archiveTime.hour,
                  label: getHourLabel(archiveTime.hour),
                }}
                onChange={(option: any) =>
                  onFieldChange(
                    setting._id,
                    "retentionTimeUnit",
                    getArchiveTimeValue(
                      option?.value || "",
                      archiveTime.minute,
                      archiveTime.second,
                    ),
                  )
                }
              />
            </div>
 
            <div>
              <label className="text-sm font-medium text-gray-600 mb-2 block">
                Archive Minute *
              </label>
              <SearchableSelect
                placeholder="Select Minute"
                searchPlaceholder="Search minute..."
                options={minuteOptions}
                value={{
                  value: archiveTime.minute,
                  label: getMinuteLabel(archiveTime.minute),
                }}
                onChange={(option: any) =>
                  onFieldChange(
                    setting._id,
                    "retentionTimeUnit",
                    getArchiveTimeValue(
                      archiveTime.hour,
                      option?.value || "",
                      archiveTime.second,
                    ),
                  )
                }
              />
            </div>
 
            <div>
              <label className="text-sm font-medium text-gray-600 mb-2 block">
                Archive Second *
              </label>
              <SearchableSelect
                placeholder="Select Second"
                searchPlaceholder="Search second..."
                options={secondOptions}
                value={{
                  value: archiveTime.second,
                  label: getSecondLabel(archiveTime.second),
                }}
                onChange={(option: any) =>
                  onFieldChange(
                    setting._id,
                    "retentionTimeUnit",
                    getArchiveTimeValue(
                      archiveTime.hour,
                      archiveTime.minute,
                      option?.value || "",
                    ),
                  )
                }
              />
            </div>
          </div>
 
          <div>
            <label className="text-sm font-medium text-gray-600 mb-2 block">
              Description
            </label>
            <textarea
              value={description}
              onChange={(event) =>
                onFieldChange(setting._id, "description", event.target.value)
              }
              rows={3}
              className="w-full px-4 py-3 border-2 outline-0 hover:border-blue-200 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
 
          <label className="inline-flex items-center gap-3 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) =>
                onFieldChange(setting._id, "isActive", event.target.checked)
              }
              className="h-4 w-4 text-blue-600 border-slate-300 rounded"
            />
            Active
          </label>
        </div>
 
        <div className="px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
 
          <button
            onClick={onSave}
            disabled={isSaving}
            className={`min-w-28 px-6 py-2 flex items-center justify-center gap-2 rounded-lg text-white transition ${
              isSaving
                ? "bg-blue-400 opacity-70 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isSaving ? "Updating..." : hasSavedSettings ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
 
export default RetentionSettingsModal;